/**
 * /finance/approvals — rounds the caller may decide (default or alternate approver, the manager it was escalated to,
 * or the original approver after an escalation) and, for finance admins, the finance-admin queue (rounds escalated
 * with no usable manager, or still waiting at the manager).
 */
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ExpenseDetailModal from "../../components/finance/ExpenseDetailModal";
import { useFinancePayeeNames } from "../../hooks/useFinancePayeeNames";
import { useAuth } from "../../contexts/AuthContext";
import {
  financeErrorMessage,
  listExpensesByIds,
  listFinanceAdminQueue,
  listMyOpenApprovalsAny,
  listStaffDirectory,
  money,
  personName,
} from "../../services/finance.service";
import type {
  ApprovalRound,
  FinanceAccess,
  StaffPerson,
} from "../../types/finance.types";

const daysSince = (iso: string) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

export default function FinanceApprovals() {
  const access = useOutletContext<FinanceAccess>();
  const { user } = useAuth();
  const me = user?.id ?? "";
  const [openId, setOpenId] = useState<string | null>(null);
  const mineQ = useQuery({
    queryKey: ["finance-approvals", me],
    queryFn: () => listMyOpenApprovalsAny(me),
    enabled: !!me,
  });
  const queueQ = useQuery({
    queryKey: ["finance-approvals", "queue"],
    queryFn: listFinanceAdminQueue,
    enabled: !!access.is_finance_admin,
  });
  const mine = useMemo(
    () =>
      (mineQ.data ?? []).filter(
        (r) =>
          r.assignee_id === me ||
          r.alternate_assignee_id === me ||
          ((r.escalation_level ?? 0) > 0 && r.original_assignee_id === me),
      ),
    [mineQ.data, me],
  );
  const ids = useMemo(
    () =>
      Array.from(
        new Set([...mine, ...(queueQ.data ?? [])].map((r) => r.expense_id)),
      ),
    [mine, queueQ.data],
  );
  const expensesQ = useQuery({
    queryKey: ["finance-expenses", "approvals", ids],
    queryFn: () => listExpensesByIds(ids),
  });
  const peopleQ = useQuery({
    queryKey: ["finance-staff"],
    queryFn: listStaffDirectory,
    staleTime: 300_000,
  });
  const people = useMemo(
    () =>
      new Map<string, StaffPerson>((peopleQ.data ?? []).map((p) => [p.id, p])),
    [peopleQ.data],
  );
  const byId = useMemo(
    () => new Map((expensesQ.data ?? []).map((x) => [x.id, x])),
    [expensesQ.data],
  );
  const payeeName = useFinancePayeeNames(
    (expensesQ.data ?? []).map((x) => x.payee_id),
  );

  const table = (rounds: ApprovalRound[], testId: string, empty: string) =>
    rounds.length === 0 ? (
      <p className="rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-gray-500">
        {empty}
      </p>
    ) : (
      <div
        className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
        data-testid={testId}
      >
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Expense
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Stage
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Requested by
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Vendor
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-500">
                Amount
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Waiting
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rounds.map((r) => {
              const x = byId.get(r.expense_id);
              return (
                <tr
                  key={r.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setOpenId(r.expense_id)}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-primary-600">
                      {x?.expense_no ?? "…"}
                    </div>
                    <div className="max-w-xs truncate text-xs text-gray-500">
                      {x?.description}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {r.stage === "actual_review"
                      ? "Actual (over the limit / corrected)"
                      : "Anticipated"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {personName(people, r.requested_by)}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {payeeName(x?.payee_id)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(r.amount_at_request)}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {daysSince(r.requested_at) === 0
                      ? "Today"
                      : `${daysSince(r.requested_at)} days`}
                    {r.escalation_target === "manager" && (
                      <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
                        escalated
                      </span>
                    )}
                    {r.escalation_target === "finance_admin_queue" && (
                      <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700">
                        finance queue
                      </span>
                    )}
                    {r.alternate_assignee_id === me && r.assignee_id !== me && (
                      <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">
                        as alternate
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

  if (mineQ.isLoading) {
    return <p className="py-8 text-center text-sm text-gray-500">Loading…</p>;
  }
  if (mineQ.error) {
    return (
      <p className="py-8 text-center text-sm text-red-700">
        {financeErrorMessage(mineQ.error)}
      </p>
    );
  }
  return (
    <div className="space-y-6" data-testid="finance-approvals-page">
      <p className="text-sm text-gray-500">
        Approving an anticipated expense authorizes the spend; approving an
        actual accepts the invoice amount. Return to ask for changes. Approvals
        waiting too long are escalated to your manager, then to finance.
      </p>
      {table(
        mine,
        "finance-my-approvals",
        "Nothing is waiting for your approval.",
      )}
      {access.is_finance_admin && (
        <section className="space-y-2">
          <h3 className="text-base font-semibold text-gray-900">
            Finance-admin queue
          </h3>
          <p className="text-sm text-gray-500">
            Escalated with no usable manager, or still waiting after the
            manager. Open one to reassign it or to approve / return it as a
            recorded finance override.
          </p>
          {table(
            queueQ.data ?? [],
            "finance-admin-queue",
            "The finance-admin queue is empty.",
          )}
        </section>
      )}
      <ExpenseDetailModal expenseId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
